// クイック選択: ブラシで撫でた領域から、色が近く強いエッジを越えない範囲へ領域成長する。
// 縮小プロキシ (長辺 ≤ 1024) 上で動かし、最後に全解像度へ拡大する。純ロジック。
import type { Rect } from '@/types/geometry'
import { trimMask, type MaskData } from './mask'

export interface QuickProxy {
  w: number
  h: number
  /** プロキシ → 文書の倍率 (文書 px / プロキシ px) */
  scale: number
  rgba: Uint8ClampedArray
  /** Sobel 勾配の大きさ (0..255 程度) */
  edge: Float32Array
  /** 選択の蓄積 (0/255) */
  mask: Uint8Array
}

export function buildQuickProxy(rgba: Uint8ClampedArray, width: number, height: number, maxSide = 1024): QuickProxy {
  const scale = Math.max(1, Math.max(width, height) / maxSide)
  const w = Math.max(1, Math.round(width / scale)), h = Math.max(1, Math.round(height / scale))
  const out = new Uint8ClampedArray(w * h * 4)
  if (scale === 1) out.set(rgba)
  else {
    // 面積平均
    for (let y = 0; y < h; y++) {
      const y0 = Math.floor(y * scale), y1 = Math.max(y0 + 1, Math.floor((y + 1) * scale))
      for (let x = 0; x < w; x++) {
        const x0 = Math.floor(x * scale), x1 = Math.max(x0 + 1, Math.floor((x + 1) * scale))
        let r = 0, g = 0, b = 0, a = 0, n = 0
        for (let yy = y0; yy < y1 && yy < height; yy++) for (let xx = x0; xx < x1 && xx < width; xx++) {
          const i = (yy * width + xx) * 4
          r += rgba[i]!; g += rgba[i + 1]!; b += rgba[i + 2]!; a += rgba[i + 3]!; n++
        }
        const o = (y * w + x) * 4
        if (n) { out[o] = r / n; out[o + 1] = g / n; out[o + 2] = b / n; out[o + 3] = a / n }
      }
    }
  }
  // 輝度の Sobel
  const lum = new Float32Array(w * h)
  for (let i = 0; i < w * h; i++) lum[i] = 0.299 * out[i * 4]! + 0.587 * out[i * 4 + 1]! + 0.114 * out[i * 4 + 2]!
  const edge = new Float32Array(w * h)
  const at = (x: number, y: number) => lum[Math.max(0, Math.min(h - 1, y)) * w + Math.max(0, Math.min(w - 1, x))]!
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const gx = -at(x - 1, y - 1) - 2 * at(x - 1, y) - at(x - 1, y + 1) + at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1)
    const gy = -at(x - 1, y - 1) - 2 * at(x, y - 1) - at(x + 1, y - 1) + at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1)
    edge[y * w + x] = Math.hypot(gx, gy) / 4
  }
  return { w, h, scale, rgba: out, edge, mask: new Uint8Array(w * h) }
}

export interface QuickDab {
  /** プロキシ座標 */
  x: number
  y: number
  radius: number
  /** 色の許容値 (0..255) */
  tolerance: number
  /** これ以上の勾配は壁 */
  edgeThreshold: number
  subtract?: boolean
}

/** ダブ 1 回: ブラシ円内をシードに、似た色を辿って広げる。変更があれば true */
export function quickSelectDab(p: QuickProxy, d: QuickDab): boolean {
  const { w, h } = p
  const r = Math.max(1, d.radius)
  const x0 = Math.max(0, Math.floor(d.x - r)), x1 = Math.min(w - 1, Math.ceil(d.x + r))
  const y0 = Math.max(0, Math.floor(d.y - r)), y1 = Math.min(h - 1, Math.ceil(d.y + r))
  if (x1 < x0 || y1 < y0) return false
  // シードの平均色
  let sr = 0, sg = 0, sb = 0, n = 0
  const seeds: number[] = []
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
    if (Math.hypot(x - d.x, y - d.y) > r) continue
    const i = y * w + x
    seeds.push(i)
    sr += p.rgba[i * 4]!; sg += p.rgba[i * 4 + 1]!; sb += p.rgba[i * 4 + 2]!; n++
  }
  if (!n) return false
  sr /= n; sg /= n; sb /= n
  const tol = d.tolerance
  const maxDist = r * 4
  const target = d.subtract ? 0 : 255
  const visited = new Uint8Array(w * h)
  const queue: number[] = []
  for (const i of seeds) {
    visited[i] = 1
    queue.push(i)
  }
  let changed = false
  const similar = (i: number) => {
    const o = i * 4
    if (p.rgba[o + 3]! < 8) return false
    return Math.abs(p.rgba[o]! - sr) <= tol && Math.abs(p.rgba[o + 1]! - sg) <= tol && Math.abs(p.rgba[o + 2]! - sb) <= tol
  }
  let head = 0
  while (head < queue.length) {
    const i = queue[head++]!
    const x = i % w, y = (i - x) / w
    const inDisk = Math.hypot(x - d.x, y - d.y) <= r
    // ブラシ円内は無条件、外は「似た色 かつ エッジでない かつ 近い」
    if (!inDisk && (!similar(i) || p.edge[i]! > d.edgeThreshold || Math.hypot(x - d.x, y - d.y) > maxDist)) continue
    if (p.mask[i] !== target) {
      p.mask[i] = target
      changed = true
    }
    const nb = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1]
    for (const j of nb) {
      if (j < 0 || visited[j]) continue
      visited[j] = 1
      queue.push(j)
    }
  }
  return changed
}

/** プロキシのマスクを文書解像度へ (バイリニア)。空なら null */
export function quickMaskToFull(p: QuickProxy, width: number, height: number): MaskData | null {
  const data = new Uint8ClampedArray(width * height)
  if (p.scale === 1) {
    for (let i = 0; i < width * height; i++) data[i] = p.mask[i]!
  } else {
    for (let y = 0; y < height; y++) {
      const fy = Math.min(p.h - 1.001, (y + 0.5) / p.scale - 0.5)
      const y0 = Math.max(0, Math.floor(fy)), ty = Math.max(0, fy - y0), y1 = Math.min(p.h - 1, y0 + 1)
      for (let x = 0; x < width; x++) {
        const fx = Math.min(p.w - 1.001, (x + 0.5) / p.scale - 0.5)
        const x0 = Math.max(0, Math.floor(fx)), tx = Math.max(0, fx - x0), x1 = Math.min(p.w - 1, x0 + 1)
        const v = (p.mask[y0 * p.w + x0]! * (1 - tx) + p.mask[y0 * p.w + x1]! * tx) * (1 - ty) + (p.mask[y1 * p.w + x0]! * (1 - tx) + p.mask[y1 * p.w + x1]! * tx) * ty
        data[y * width + x] = v
      }
    }
  }
  return trimMask({ rect: { x: 0, y: 0, w: width, h: height }, data })
}
